import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * RelatedTagsRenderer — orchestrator for the related-tags feature.
 *
 * Initialisation flow:
 *  1. Scan the page for new-style mount points (`.related-tags-root[data-tags-json]`).
 *  2. For each mount point, mount a React island that uses the view contract
 *     resolved via `@hooks/relatedTagsView` (project override → core default).
 *
 * Guards against double-initialisation: each root element is marked with
 * `data-rtags-init` after mounting.
 *
 * Visibility strategy:
 *  - The root element is immediately hidden (visibility: hidden) in the
 *    initialiser, BEFORE React renders into it. This prevents any server‑side
 *    or DOM‑content flash (the “all tags visible initially” problem).
 *  - Once the useTagsLayout hook has finished its first measurement pass
 *    and `measured` becomes true, the island removes the `visibility` style
 *    from the root element, revealing the correctly‑truncated tag list.
 */
import React, { useState, useLayoutEffect, useCallback, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { readAllRelatedTagsBootstrap } from './relatedTagsBootstrap';
import { calculateVisibleCount } from './tagLayoutService';
// ─── Shared hook: layout measurement & expand/collapse ─────────────────────────
function useTagsLayout(items, containerRef, measurementRef) {
    const [expanded, setExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(items.length);
    const [measured, setMeasured] = useState(false);
    // Keep a ref to expanded so the ResizeObserver callback always sees the latest value.
    const expandedRef = useRef(expanded);
    expandedRef.current = expanded;
    const recalculate = useCallback(() => {
        if (!containerRef.current || !measurementRef.current) {
            return;
        }
        const containerWidth = containerRef.current.offsetWidth;
        // Measure item widths from the hidden measurement rail (all items visible, no d-none).
        const itemEls = Array.from(measurementRef.current.querySelectorAll('.tag-badge:not(.tag-badge-more)'));
        const itemWidths = itemEls.map((el) => el.offsetWidth);
        // Toggle width is measured from a standalone .tag-badge-more rendered in the rail.
        const toggleEl = measurementRef.current.querySelector('.tag-badge-more');
        const toggleWidth = toggleEl ? toggleEl.offsetWidth : 0;
        const { visibleCount: vc } = calculateVisibleCount(containerWidth, itemWidths, toggleWidth, {
            minVisibleWhenCollapsed: 1,
        });
        setVisibleCount(vc);
        setMeasured(true);
    }, [containerRef, measurementRef]);
    // Initial measurement once the hidden measurement rail is in the DOM.
    useEffect(() => {
        if (measurementRef.current) {
            recalculate();
        }
    }, [items, recalculate, measurementRef]);
    // ResizeObserver on the visual container – recalculates only in collapsed state.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const observer = new ResizeObserver(() => {
            if (!expandedRef.current) {
                recalculate();
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [containerRef, recalculate]);
    const onToggle = useCallback(() => {
        setExpanded((prev) => {
            const next = !prev;
            if (!next) {
                // After collapsing we need to re‑measure because the visible list may have changed.
                requestAnimationFrame(() => {
                    recalculate();
                });
            }
            return next;
        });
    }, [recalculate]);
    return { expanded, visibleCount, onToggle, measured };
}
const RelatedTagsIsland = ({ items, contract, containerRef }) => {
    // Guard against invalid contract (e.g., when @hooks/relatedTagsView returns null)
    if (!contract || !contract.ContainerComponent || !contract.ToggleComponent) {
        console.error('[RelatedTagsIsland] Invalid contract received:', contract);
        return null;
    }
    const { ContainerComponent, ToggleComponent, messages } = contract;
    const measurementRef = useRef(null);
    const { expanded, visibleCount, onToggle, measured } = useTagsLayout(items, containerRef, measurementRef);
    // Reveal the root container once layout measurement is ready.
    useLayoutEffect(() => {
        if (measured && containerRef.current) {
            containerRef.current.style.visibility = '';
        }
    }, [measured, containerRef]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: measurementRef, style: { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }, "aria-hidden": "true", children: [_jsx(ContainerComponent, { items: items, visibleCount: items.length, expanded: true, onToggle: () => { }, messages: messages, ToggleComponent: ToggleComponent }), _jsx(ToggleComponent, { hiddenCount: 1, expanded: false, onToggle: () => { }, messages: messages })] }), _jsx(ContainerComponent, { items: items, visibleCount: visibleCount, expanded: expanded, onToggle: onToggle, messages: messages, ToggleComponent: ToggleComponent }, `${visibleCount}-${expanded}`)] }));
};
/**
 * Self‑contained tags block for use inside React‑rendered article cards.
 */
export const RelatedTagsInlineBlock = ({ items, contract, className, }) => {
    // Guard against invalid contract (e.g., when @hooks/relatedTagsView returns null)
    if (!contract || !contract.ContainerComponent || !contract.ToggleComponent) {
        console.error('[RelatedTagsInlineBlock] Invalid contract received:', contract);
        return null;
    }
    const { ContainerComponent, ToggleComponent, messages } = contract;
    const wrapperRef = useRef(null);
    const measurementRef = useRef(null);
    const { expanded, visibleCount, onToggle, measured } = useTagsLayout(items, wrapperRef, measurementRef);
    if (!items.length)
        return null;
    return (_jsxs("div", { ref: wrapperRef, className: className, style: Object.assign({ position: 'relative' }, (measured ? undefined : { visibility: 'hidden' })), children: [_jsxs("div", { ref: measurementRef, style: { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }, "aria-hidden": "true", children: [_jsx(ContainerComponent, { items: items, visibleCount: items.length, expanded: true, onToggle: () => { }, messages: messages, ToggleComponent: ToggleComponent }), _jsx(ToggleComponent, { hiddenCount: 1, expanded: false, onToggle: () => { }, messages: messages })] }), _jsx(ContainerComponent, { items: items, visibleCount: visibleCount, expanded: expanded, onToggle: onToggle, messages: messages, ToggleComponent: ToggleComponent }, `${visibleCount}-${expanded}`)] }));
};
// ── Public initialiser ─────────────────────────────────────────────────────────
const INIT_ATTR = 'data-rtags-init';
/**
 * Initialise related‑tags islands.
 *
 * @param contract  Resolved view contract from `@hooks/relatedTagsView`.
 */
export async function initRelatedTagsRenderer(contract) {
    if (!contract || !contract.ContainerComponent || !contract.ToggleComponent || !contract.messages) {
        console.error('initRelatedTagsRenderer: invalid contract received', contract);
        console.error('Make sure @hooks/relatedTagsView exports a default function that returns a valid contract');
        return;
    }
    const bootstraps = readAllRelatedTagsBootstrap();
    if (bootstraps.length === 0) {
        return;
    }
    for (const [i, { rootElement, items }] of bootstraps.entries()) {
        if (rootElement.hasAttribute(INIT_ATTR)) {
            continue; // already initialised
        }
        // Prevent flash: hide root, set position:relative for absolute measurement rail
        rootElement.style.visibility = 'hidden';
        rootElement.style.position = 'relative';
        rootElement.setAttribute(INIT_ATTR, '1');
        const containerRefObj = { current: rootElement };
        const root = createRoot(rootElement);
        root.render(React.createElement(RelatedTagsIsland, {
            items,
            contract,
            containerRef: containerRefObj,
        }));
    }
}
